import { createContext, useContext, useState, useCallback, useEffect, useMemo, type ReactNode } from 'react';
import { MotionConfig, MotionGlobalConfig } from 'framer-motion';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { defaultAnimationConfig, type AnimationConfig } from '../../config/animation.config';

// Motion policy (owner request, 2026-09): animations run ONLY on the home page;
// everywhere else every framer-motion animation jumps straight to its end state.
// The flag is flipped per route by setHomeMotion() (called from AppRoutes), and
// the matching CSS-animation switch is the html[data-home-motion] attribute.
//
// isHomePath stays exactly as it was because SmoothScrollProvider uses it to decide
// where Lenis runs, and Lenis must NOT run on the market console: it fights the sticky
// section rail and the scroll-spy observer.
export const isHomePath = (pathname: string) => pathname === '/' || pathname === '';

// 2026-09-24: the market console is the second surface allowed to animate. Its whole
// design is motion carrying meaning -- a range drawing itself, a marker settling onto
// it, a figure counting up to what was measured. With the policy applied literally
// every one of those snapped to its end state and the page read as static.
export const isMotionPath = (pathname: string) =>
  isHomePath(pathname) || pathname.startsWith('/market-tools');

export function setHomeMotion(pathname: string) {
  const on = isMotionPath(pathname);
  MotionGlobalConfig.skipAnimations = !on;
  if (typeof document !== 'undefined') {
    document.documentElement.toggleAttribute('data-home-motion', on);
  }
}

interface MotionContextValue {
  /** Whether animations are currently reduced (system preference or user disabled) */
  reducedMotion: boolean;
  /** Whether the user has manually disabled animations */
  userDisabledAnimations: boolean;
  /** Disable all animations (user preference) */
  disableAnimations: () => void;
  /** Enable animations (if not blocked by system preference) */
  enableAnimations: () => void;
  /** Toggle animation state */
  toggleAnimations: () => void;
  /** Get animation config with reduced motion applied */
  getAnimationConfig: () => AnimationConfig;
}

const MotionContext = createContext<MotionContextValue | undefined>(undefined);

interface MotionProviderProps {
  children: ReactNode;
}

export function MotionProvider({ children }: MotionProviderProps) {
  const systemReducedMotion = useReducedMotion();
  const [userDisabledAnimations, setUserDisabledAnimations] = useState(false);

  // Animations are reduced if either system preference or user choice
  const reducedMotion = systemReducedMotion || userDisabledAnimations;

  const disableAnimations = useCallback(() => {
    setUserDisabledAnimations(true);
    // Persist preference
    try {
      localStorage.setItem('safaqat24-reduce-motion', 'true');
    } catch {
      // localStorage might not be available
    }
  }, []);

  const enableAnimations = useCallback(() => {
    setUserDisabledAnimations(false);
    // Persist preference
    try {
      localStorage.setItem('safaqat24-reduce-motion', 'false');
    } catch {
      // localStorage might not be available
    }
  }, []);

  const toggleAnimations = useCallback(() => {
    if (userDisabledAnimations) {
      enableAnimations();
    } else {
      disableAnimations();
    }
  }, [userDisabledAnimations, enableAnimations, disableAnimations]);

  const getAnimationConfig = useCallback((): AnimationConfig => {
    if (reducedMotion) {
      return {
        ...defaultAnimationConfig,
        enabled: false,
        reducedMotion: true,
        duration: 0,
      };
    }
    return defaultAnimationConfig;
  }, [reducedMotion]);

  // Load user preference on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem('safaqat24-reduce-motion');
      if (stored === 'true') {
        setUserDisabledAnimations(true);
      }
    } catch {
      // localStorage might not be available
    }
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      reducedMotion,
      userDisabledAnimations,
      disableAnimations,
      enableAnimations,
      toggleAnimations,
      getAnimationConfig,
    }),
    [reducedMotion, userDisabledAnimations, disableAnimations, enableAnimations, toggleAnimations, getAnimationConfig]
  );

  // `reducedMotion` here drives ALL framer-motion animations app-wide:
  //  - 'always' when the user has manually disabled animations (force reduce)
  //  - 'user'   otherwise → follows the OS prefers-reduced-motion setting
  // framer-motion then skips transform/layout animations (opacity still fades),
  // which covers the JS-driven motion that CSS @media reduced-motion can't reach
  // (hero entrance, nav hover/tap, the infinite notification-bell shake, etc.).
  return (
    <MotionContext.Provider value={contextValue}>
      <MotionConfig reducedMotion={userDisabledAnimations ? 'always' : 'user'}>
        {children}
      </MotionConfig>
    </MotionContext.Provider>
  );
}

export function useMotion() {
  const context = useContext(MotionContext);
  if (context === undefined) {
    throw new Error('useMotion must be used within a MotionProvider');
  }
  return context;
}

export default MotionProvider;
